import { LogisticsProjectLookupComponent } from '../../logistics-projects/logistics-project-lookup.component';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ChartOfAccounts } from '../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../chart-of-accounts/chart-of-accounts.service';
import { Company } from '../../company/company.model';
import { Customer } from '../../customer/customer.model';
import { PartyType } from '../../party/party-type.enum';
import { Country } from '../../models/common/country.model';
import { Vehicle } from '../../fleet/vehicle/vehicle.model';
import { VehicleService } from '../../fleet/vehicle/vehicle.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference, normalizeCommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { CountryCurrencySelectComponent } from '../../shared/country-currency-select/country-currency-select.component';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { ExpenseAttachment } from '../expense-attachment.model';
import { Expense } from '../expense.model';
import { ExpenseService } from '../expense.service';

@Component({
    selector: 'app-add-edit-expense',
    standalone: true,
    templateUrl: './add-edit-expense.component.html',
    styleUrls: ['./add-edit-expense.component.css'],
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ProgressBarModule,
        InputTextModule,
        ButtonModule,
        SelectModule,
        BreadcrumbModule,
        DatePickerModule,
        InputNumberModule,
        TextareaModule,
        FileUploadModule,
        TableModule,
        CheckboxModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageFormSectionComponent,
        CommercialPartyPickerComponent,
        CountryCurrencySelectComponent
    ],
    providers: [
        MessageService,
        ExpenseService,
        ChartOfAccountsService,
        VehicleService
    ]
})
export class AddEditExpenseComponent implements OnInit, OnDestroy {
    private readonly ngZone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);
    expenseForm!: FormGroup;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    isEditMode = false;
    isLoading = false;
    submitted = false;
    currentAction: 'save' | 'submit' | 'post' | null = null;
    canPostExpense = false;

    private expenseId: number | null = null;
    private readonly destroy$ = new Subject<void>();

    defaultCompany: Company | undefined;
    defaultCountry: Country | undefined;
    readonly expensePartyRoles: string[] = [];
    commercialPartyReference: CommercialPartyReference = normalizeCommercialPartyReference();
    expenseOwningCompany: Company | undefined;
    private expenseSubmitter: Customer | null = null;
    private originalVendor: Company | undefined;
    private loadedCrmClientId: number | null = null;
    chartOfAccounts: ChartOfAccounts[] = [];
    creditAccountOptions: Array<{ label: string; value: number }> = [];

    vehicles: Vehicle[] = [];
    vehicleOptions: Array<{ label: string; value: number }> = [];
    attachments: ExpenseAttachment[] = [];

    constructor(
        private fb: FormBuilder,
        private expenseService: ExpenseService,
        private messageService: MessageService,
        private chartOfAccountsService: ChartOfAccountsService,
        private vehicleService: VehicleService,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        private logisticsProjectsService: LogisticsProjectsService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.canPostExpense = this.permissionService.hasPermission(app_permissions.expense.posting.post);
        this.defaultCompany = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
        this.defaultCountry = this.sessionStorageService.getCountryList()
            ?.find(country => country.id === this.defaultCompany?.countryId);
        this.expenseOwningCompany = this.defaultCompany;
        this.expenseSubmitter = this.sessionStorageService.getCustomer() ?? null;

        this.initializeForm();
        this.loadInitialData();
        this.subscribeToItemChanges();
        this.checkRouteForEditMode();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private syncView(work?: () => void): void {
        this.ngZone.run(() => {
            work?.();
            this.cdr.detectChanges();
        });
    }

    get expenseItems(): FormArray {
        return this.expenseForm.get('expenseItems') as FormArray;
    }

    addExpenseItem(): void {
        this.expenseItems.push(this.createExpenseItemFormGroup());
    }

    removeExpenseItem(index: number): void {
        if (this.expenseItems.length > 1) {
            this.expenseItems.removeAt(index);
            this.updateTotalAmount();
            return;
        }

        this.messageService.add({ severity: 'warn', summary: 'Expense Items', detail: 'At least one line item is required.' });
    }

    saveExpense(): void {
        this.submitted = true;
        if (!this.hasRequiredContext()) return;
        if (this.expenseForm.invalid) {
            this.expenseForm.markAllAsTouched();
            this.messageService.add({ severity: 'error', summary: 'Validation', detail: 'Review the highlighted fields before saving.' });
            return;
        }

        this.currentAction = 'save';
        this.isLoading = true;
        const payload = this.createExpensePayload();

        const operation = this.isEditMode
            ? this.expenseService.updateExpense(this.expenseId!, payload)
            : this.expenseService.createExpense(payload);

        operation.pipe(takeUntil(this.destroy$)).subscribe({
            next: response => {
                this.syncView(() => {
                    this.messageService.add({ severity: 'success', summary: 'Expense Saved', detail: `Expense ${this.isEditMode ? 'updated' : 'created'} successfully.` });
                    this.currentAction = null;
                    this.isLoading = false;
                });
                this.router.navigate(['/expense/view', response.expenseId]);
            },
            error: () => {
                this.syncView(() => {
                    this.currentAction = null;
                    this.isLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Expense Save Failed', detail: 'The expense could not be saved.' });
                });
            }
        });
    }

    submitForApproval(): void {
        this.submitted = true;
        if (!this.hasRequiredContext()) return;
        if (this.expenseForm.invalid) {
            this.expenseForm.markAllAsTouched();
            this.messageService.add({ severity: 'error', summary: 'Validation', detail: 'Complete the expense details before submitting.' });
            return;
        }

        this.currentAction = 'submit';
        this.isLoading = true;
        const payload = this.createExpensePayload();

        const save$ = this.isEditMode
            ? this.expenseService.updateExpense(this.expenseId!, payload)
            : this.expenseService.createExpense(payload);

        save$.pipe(
            tap(savedExpense => {
                this.expenseId = savedExpense.expenseId!;
            }),
            switchMap(savedExpense => this.expenseService.submitExpenseForApproval(savedExpense.expenseId!)),
            takeUntil(this.destroy$)
        ).subscribe({
            next: response => {
                this.syncView(() => {
                    this.messageService.add({ severity: 'success', summary: 'Submitted', detail: 'Expense submitted for approval.' });
                    this.currentAction = null;
                    this.isLoading = false;
                });
                this.router.navigate(['/expense/view', response.expenseId ?? this.expenseId]);
            },
            error: () => {
                this.syncView(() => {
                    this.currentAction = null;
                    this.isLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Submission Failed', detail: 'The expense could not be submitted for approval.' });
                });
            }
        });
    }

    postExpense(): void {
        if (this.isLoading || !this.canPostExpense || this.isEditMode) return;
        this.submitted = true;
        if (!this.hasRequiredContext()) return;
        if (this.expenseForm.invalid) {
            this.expenseForm.markAllAsTouched();
            this.messageService.add({ severity: 'error', summary: 'Validation', detail: 'Complete the expense and choose a credit account before posting.' });
            return;
        }

        this.currentAction = 'post';
        this.isLoading = true;
        this.expenseService.createAndPostExpense(this.createExpensePayload())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    this.syncView(() => {
                        this.currentAction = null;
                        this.isLoading = false;
                        this.messageService.add({ severity: 'success', summary: 'Expense Posted', detail: 'Expense posted to the general ledger.' });
                    });
                    this.router.navigate(['/expense/view', response.expenseId]);
                },
                error: error => {
                    this.syncView(() => {
                        this.currentAction = null;
                        this.isLoading = false;
                        this.messageService.add({ severity: 'error', summary: 'Posting Failed',
                            detail: error?.error?.message || 'The expense could not be posted.' });
                    });
                }
            });
    }

    discardExpense(): void {
        if (this.isEditMode && this.expenseId) {
            this.router.navigate(['/expense/view', this.expenseId]);
            return;
        }

        this.resetForm();
        this.messageService.add({ severity: 'info', summary: 'Form Reset', detail: 'Expense draft cleared.' });
    }

    onAttachmentsSelect(event: FileSelectEvent): void {
        for (const file of event.files) {
            file.text().then(value => {
                this.attachments.push({
                    fileName: file.name,
                    filePath: `temp/uploads/${Date.now()}_${file.name}`,
                    fileType: file.type,
                    fileSize: file.size,
                    fileContent: value
                });
                this.messageService.add({ severity: 'info', summary: 'Attachment Added', detail: `${file.name} is ready to save with the expense.` });
            }).catch(() => {
                this.messageService.add({ severity: 'error', summary: 'Attachment Failed', detail: `${file.name} could not be prepared for upload.` });
            });
        }
    }

    removeAttachment(index: number): void {
        const removedFileName = this.attachments[index]?.fileName;
        this.attachments.splice(index, 1);
        if (removedFileName) {
            this.messageService.add({ severity: 'warn', summary: 'Attachment Removed', detail: `${removedFileName} removed from the draft.` });
        }
    }

    hasError(controlName: string): boolean {
        const control = this.expenseForm.get(controlName);
        return !!control && control.invalid && (this.submitted || control.touched || control.dirty);
    }

    hasItemError(index: number, controlName: string): boolean {
        const control = this.expenseItems.at(index)?.get(controlName);
        return !!control && control.invalid && (this.submitted || control.touched || control.dirty);
    }

    isActionLoading(action: 'save' | 'submit' | 'post'): boolean {
        return this.isLoading && this.currentAction === action;
    }

    private initializeForm(): void {
        const defaultCurrency = this.defaultCountry?.currencyCode || 'SAR';

        this.expenseForm = this.fb.group({
            expenseId: [null],
            expenseNumber: [{ value: '', disabled: true }],
            submissionDate: [new Date(), Validators.required],
            totalAmount: [{ value: 0, disabled: true }],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            currencyCode: [defaultCurrency, Validators.required],
            creditAccountId: [null, Validators.required],
            description: ['', Validators.required],
            status: [{ value: 'Draft', disabled: true }],
            notes: [''],
            expenseItems: this.fb.array([])
        });
    }

    private createExpenseItemFormGroup(item?: any): FormGroup {
        return this.fb.group({
            expenseItemId: [item?.expenseItemId || null],
            itemDate: [item?.itemDate ? new Date(item.itemDate) : new Date(), Validators.required],
            account: [item?.account || null, Validators.required],
            amount: [item?.amount || 0, [Validators.required, Validators.min(0.01)]],
            logisticsProjectId: [item?.logisticsProjectId ?? this.expenseForm?.get('logisticsProjectId')?.value ?? null],
            vehicleId: [item?.vehicleId ?? null],
            description: [item?.description || '', Validators.required],
            taxAmount: [item?.taxAmount || 0],
            isReimbursable: [item?.isReimbursable ?? true]
        });
    }

    private subscribeToItemChanges(): void {
        this.expenseItems.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateTotalAmount());
    }

    private updateTotalAmount(): void {
        const total = this.expenseItems.controls.reduce((sum, control) => {
            const amount = Number(control.get('amount')?.value || 0);
            const taxAmount = Number(control.get('taxAmount')?.value || 0);
            return sum + amount + taxAmount;
        }, 0);
        this.expenseForm.get('totalAmount')?.setValue(total, { emitEvent: false });
    }

    private resetForm(): void {
        this.submitted = false;
        this.currentAction = null;
        this.attachments = [];
        this.expenseItems.clear();
        this.expenseForm.reset({
            expenseId: null,
            expenseNumber: '',
            submissionDate: new Date(),
            totalAmount: 0,
            logisticsProjectId: Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null,
            currencyCode: this.defaultCountry?.currencyCode || 'SAR',
            creditAccountId: null,
            description: '',
            status: 'Draft',
            notes: ''
        });
        this.addExpenseItem();
        this.updateTotalAmount();
        this.expenseOwningCompany = this.defaultCompany;
        this.expenseSubmitter = this.sessionStorageService.getCustomer() ?? null;
        this.originalVendor = undefined;
        this.loadedCrmClientId = null;
        this.commercialPartyReference = normalizeCommercialPartyReference();
    }

    private loadInitialData(): void {
        this.isLoading = true;
        const activeCompany = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
        const companyId = activeCompany?.id ?? 0;
        const transportationAgencyId = activeCompany?.transportationAgencyId;

        forkJoin({
            accounts: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false),
            ownedVehicles: transportationAgencyId
                ? this.vehicleService.findVehiclesByTransportationAgencyId(0, 500, transportationAgencyId).pipe(catchError(() => of({ items: [] } as any)))
                : of({ items: [] } as any),
            freelanceVehicles: this.vehicleService.getFreelanceVehiclesByCompanyId(0, 500, companyId).pipe(catchError(() => of({ items: [] } as any))),
            rentalVehicles: this.vehicleService.getRentalVehiclesByCompanyId(0, 500, companyId).pipe(catchError(() => of({ items: [] } as any)))
        }).pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ accounts, ownedVehicles, freelanceVehicles, rentalVehicles }) => {
                    this.syncView(() => {
                        const accountMap = new Map<number, ChartOfAccounts>();
                        const parentIds = new Set(accounts.map(account => account.parentAccount?.accountId).filter((id): id is number => !!id));
                        const collectAccounts = (items: ChartOfAccounts[]) => items.forEach(account => {
                            if (account.childrenAccounts?.length) collectAccounts(account.childrenAccounts);
                            else if (account.accountId && !parentIds.has(account.accountId)
                                && account.isActive === true && !account.deprecated) {
                                accountMap.set(account.accountId, account);
                            }
                        });
                        collectAccounts(accounts);
                        this.chartOfAccounts = Array.from(accountMap.values());
                        this.creditAccountOptions = Array.from(accountMap.values())
                            .filter(account => account.accountType?.code?.toUpperCase() === 'AP')
                            .map(account => ({ label: `${account.accountCode} — ${account.accountName}`, value: account.accountId }))
                            .sort((a, b) => a.label.localeCompare(b.label));
                        this.vehicles = this.uniqueVehicles([
                            ...(ownedVehicles?.items ?? []),
                            ...(freelanceVehicles?.items ?? []),
                            ...(rentalVehicles?.items ?? [])
                        ]);
                        this.vehicleOptions = this.vehicles.map(vehicle => ({
                            label: this.vehicleOptionLabel(vehicle),
                            value: vehicle.vehicleId
                        }));
                        this.isLoading = false;
                    });
                },
                error: () => {
                    this.syncView(() => {
                        this.isLoading = false;
                        this.messageService.add({ severity: 'error', summary: 'Expense Form', detail: 'Required master data could not be loaded.' });
                    });
                }
            });
    }

    private checkRouteForEditMode(): void {
        this.route.paramMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                const id = params.get('id');
                if (id) {
                    this.isEditMode = true;
                    this.expenseId = Number(id);
                    this.breadcrumbItems = [
                        { label: 'Finance', routerLink: '/expense' },
                        { label: 'Expenses', routerLink: '/expense' },
                        { label: `Edit Expense #${id}` }
                    ];
                    this.loadExpenseForEdit(this.expenseId);
                    return;
                }

                this.isEditMode = false;
                this.breadcrumbItems = [
                    { label: 'Finance', routerLink: '/expense' },
                    { label: 'Expenses', routerLink: '/expense' },
                    { label: 'New Expense' }
                ];

                if (!this.expenseItems.length) {
                    this.addExpenseItem();
                }
            });
    }

    private loadExpenseForEdit(id: number): void {
        this.isLoading = true;
        this.expenseService.getExpenseById(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: expense => {
                    this.syncView(() => {
                        this.expenseForm.patchValue({
                            expenseId: expense.expenseId,
                            expenseNumber: expense.expenseNumber,
                            submissionDate: expense.submissionDate ? new Date(expense.submissionDate) : new Date(),
                            totalAmount: expense.totalAmount || 0,
                            logisticsProjectId: expense.logisticsProjectId || this.expenseForm.get('logisticsProjectId')?.value,
                            currencyCode: expense.currencyCode || this.defaultCountry?.currencyCode || 'SAR',
                            creditAccountId: expense.creditAccountId ?? null,
                            description: expense.description || '',
                            status: expense.status || 'Draft',
                            notes: expense.notes || ''
                        });
                        this.expenseOwningCompany = expense.owningCompany ?? this.defaultCompany;
                        this.expenseSubmitter = expense.submittedBy ?? this.sessionStorageService.getCustomer() ?? null;
                        this.originalVendor = expense.vendor;
                        this.loadedCrmClientId = expense.crmClientId ?? null;
                        this.commercialPartyReference = normalizeCommercialPartyReference({
                            source: expense.crmClientId ? 'CRM' : expense.partyEntityId || expense.vendor?.id ? 'PARTY' : 'CRM',
                            crmClientId: expense.crmClientId,
                            crmAccountId: expense.crmAccountId,
                            crmContactId: expense.crmClientContactId,
                            partyEntityId: expense.partyEntityId ?? expense.vendor?.id,
                            partyEntityType: expense.partyEntityType ?? (expense.vendor?.id ? PartyType.Company : null),
                            partyContactEntityId: expense.partyContactEntityId,
                            partyContactEntityType: expense.partyContactEntityType
                        });
                        this.attachments = expense.attachments || [];
                        this.expenseItems.clear();
                        expense.items?.forEach(item => this.expenseItems.push(this.createExpenseItemFormGroup(item)));
                        if (!this.expenseItems.length) {
                            this.addExpenseItem();
                        }
                        this.updateTotalAmount();
                        this.isLoading = false;
                    });
                },
                error: () => {
                    this.syncView(() => {
                        this.isLoading = false;
                        this.messageService.add({ severity: 'error', summary: 'Expense Form', detail: 'Expense data could not be loaded.' });
                    });
                    this.router.navigate(['/expense']);
                }
            });
    }

    private createExpensePayload(): Expense {
        const formValue = this.expenseForm.getRawValue();
        const { expenseItems, ...header } = formValue;
        const preserveVendor = (this.commercialPartyReference.source === 'CRM'
            && this.commercialPartyReference.crmClientId === this.loadedCrmClientId)
            || (this.commercialPartyReference.source === 'PARTY'
                && this.commercialPartyReference.partyEntityType === PartyType.Company
                && this.commercialPartyReference.partyEntityId === this.originalVendor?.id);

        return {
            ...header,
            owningCompany: this.expenseOwningCompany!,
            submittedBy: this.expenseSubmitter!,
            vendor: preserveVendor ? this.originalVendor : undefined,
            partyEntityId: this.commercialPartyReference.source === 'PARTY'
                ? this.commercialPartyReference.partyEntityId ?? undefined : undefined,
            partyEntityType: this.commercialPartyReference.source === 'PARTY'
                ? this.commercialPartyReference.partyEntityType ?? undefined : undefined,
            partyContactEntityId: this.commercialPartyReference.source === 'PARTY'
                ? this.commercialPartyReference.partyContactEntityId ?? undefined : undefined,
            partyContactEntityType: this.commercialPartyReference.source === 'PARTY'
                ? this.commercialPartyReference.partyContactEntityType ?? undefined : undefined,
            crmClientId: this.commercialPartyReference.crmClientId ?? undefined,
            crmAccountId: this.commercialPartyReference.crmAccountId ?? undefined,
            crmClientContactId: this.commercialPartyReference.crmContactId ?? undefined,
            items: expenseItems.map((item: any) => ({
                ...item,
                logisticsProjectId: item.logisticsProjectId || header.logisticsProjectId || undefined,
                vehicleId: item.vehicleId || undefined
            })),
            attachments: this.attachments.map(attachment => ({
                ...attachment,
                uploadedBy: this.sessionStorageService.getCustomer()?.id
            }))
        } as Expense;
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = normalizeCommercialPartyReference(reference);
    }

    private hasRequiredContext(): boolean {
        if (this.expenseOwningCompany?.id && this.expenseSubmitter?.id) return true;
        this.messageService.add({
            severity: 'error',
            summary: 'Expense Context',
            detail: 'A company and signed-in submitter are required to save this expense.'
        });
        return false;
    }

    vehicleOptionLabel(vehicle: Vehicle): string {
        if (!vehicle) {
            return '';
        }
        return [vehicle.plateNumber, vehicle.registrationNumber].filter(Boolean).join(' | ') || `Vehicle #${vehicle.vehicleId}`;
    }

    private uniqueVehicles(items: Vehicle[]): Vehicle[] {
        const map = new Map<number, Vehicle>();
        items.filter(vehicle => !!vehicle?.vehicleId).forEach(vehicle => {
            if (!map.has(vehicle.vehicleId)) {
                map.set(vehicle.vehicleId, vehicle);
            }
        });
        return Array.from(map.values());
    }
}
